import {ReactNode, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {ColumnDefinition, CulturalDashboardTable} from '../culture/dashboard/components/shared/CulturalDashboardTable';
import {BenefitClass} from '../culture/dashboard/types/CultureDashboardBenefitsTypes';

import {bodyPdf, pdfH1} from './styles';
import {LaptopIcon, SmileIcon, HeartbeatIcon, ChartLineIcon, StarIcon} from '../../../assets/company_benefits';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {orangeColors, empionOrangeGradient} from '../../../layouts/theme/designSystem';

interface Props {
  items: BenefitClass[];
  pageCount: string;
}

export const EmployeeDesiredBenefits = ({items, pageCount}: Props) => {
  const {t} = useTranslation('pdfDomain');
  const {t: tBenefits} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const iconFillStyles = orangeColors[70];

  const benefitIcons: {[key: string]: ReactNode} = {
    office: <LaptopIcon fill={iconFillStyles} aria-label='laptop icon' />,
    happiness: <SmileIcon fill={iconFillStyles} aria-label='smiling face icon' />,
    health: <HeartbeatIcon fill={iconFillStyles} aria-label='heartbeat icon' />,
    financial: <ChartLineIcon fill={iconFillStyles} aria-label='chart line icon' />,
  };

  const renderIcon = (category: string) => {
    return benefitIcons[category] || <StarIcon />;
  };

  const desiredBenefitsColumns: ColumnDefinition<BenefitClass>[] = [
    {
      label: tBenefits('companyCultureDashboard.desiredBenefitsDialog.benefitsWishes'),
      render: (item) => (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {renderIcon(item.category)}
          {item.benefit.labels[currentLanguage]}
        </Box>
      ),
      key: 'benefit',
    },
    {
      key: 'distribution',
      label: '#',
      render: (item) => item.distribution,
    },
    {
      key: 'percentage',
      label: '%',
      render: (item) => `${Math.round(item.percentage ? item.percentage * 100 : 0)}%`,
    },
  ];

  return (
    <>
      <Grid container sx={{background: empionOrangeGradient.dark}} p={5}>
        <Typography variant='h1' sx={pdfH1} mb={2}>
          {t('employeeDesiredBenefits.title')}
        </Typography>
        <Typography variant='h1' sx={pdfH1} mb={2} ml={1}>
          {pageCount}
        </Typography>
        <Typography
          sx={{
            ...bodyPdf,
            mb: 2,
            width: '100%',
          }}
        >
          {t('employeeDesiredBenefits.textBlock')}
        </Typography>
      </Grid>

      <Grid container px={5}>
        <CulturalDashboardTable
          data={items}
          columns={desiredBenefitsColumns}
          alternativeTitle={t('strengths.alternativeTitle') as string}
          textStyleHeaders={{fontSize: '18px'}}
          textStyleBody={bodyPdf}
        />
      </Grid>
    </>
  );
};
